import { _lcs } from "../i18n";

/**
 * Share a link: the phone's own share sheet where there is one, otherwise the
 * clipboard, otherwise a selected text field the guest can copy from.
 *
 * Shared links are the page's own URL — the house's guest-app plus the hash
 * route of an event, place or tour. Routes carry public record ids only, never
 * a session or a survey token; callers that would share anything else must
 * not use this.
 *
 * @param {Object} app        for the toasts
 * @param {Object} data       { title, text?, url? (default: the current page) }
 * @returns {Promise<"shared"|"copied"|"cancelled"|"failed">}
 */
export async function share( app, { title, text = null, url = window.location.href } )
{
    const payload = { title : title || document.title, text : text || title || "", url };

    if ( typeof navigator.share === "function" &&
         ( typeof navigator.canShare !== "function" || navigator.canShare( payload ) ) )
    {
        try
        {
            await navigator.share( payload );
            return "shared";
        }
        catch ( e )
        {
            // The guest closed the sheet: nothing to report.
            if ( e && e.name === "AbortError" )
            {
                return "cancelled";
            }
            // Anything else (e.g. NotAllowedError in an in-app browser): try copying.
        }
    }

    if ( await copy( url ) )
    {
        app.view.toast( _lcs( "Link kopiert." ), "success" );
        return "copied";
    }

    app.view.toast( _lcs( "Teilen nicht möglich." ), "error" );
    return "failed";
}

async function copy( text )
{
    try
    {
        if ( navigator.clipboard && window.isSecureContext )
        {
            await navigator.clipboard.writeText( text );
            return true;
        }
    }
    catch ( e )
    {
        // Permission refused — fall through to the old way.
    }

    // execCommand is deprecated but still the only copy on some in-app browsers.
    const field = document.createElement( "textarea" );
    field.value = text;
    field.setAttribute( "readonly", "" );
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild( field );
    field.select();
    let ok = false;
    try
    {
        ok = document.execCommand( "copy" );
    }
    catch ( e )
    {
        ok = false;
    }
    field.remove();
    return ok;
}
